import { useState } from 'react';
import { Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { useReportInvalid } from './invalidInputs';
import { parseIntInput } from './parse';

export interface IntInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<InputRef>;
  /** LinesGrid keyboard navigation (§1 «Tab и Enter переходят по полям») — см. useGridNavigation. */
  navRow?: number;
  navCol?: number;
}

/**
 * Целое число (коробки, штук в коробке, N) — коммит на blur/Enter, не на каждый символ (§1).
 * Неверный ввод не исправляется молча: текст остаётся в поле, поле краснеет и объясняет причину,
 * а наружу уходит «пусто» — документ с таким полем не провести и не сохранить как заполненный.
 */
export function IntInput({ value, onChange, min = 1, autoFocus, disabled, onKeyDown, inputRef, navRow, navCol }: IntInputProps) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const [error, setError] = useState<string | null>(null);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    // Значение сменилось снаружи (пересев окна, выбор товара) — показываем его. Исключение —
    // собственный отказ: «пусто» наружу при неверном вводе не должно стирать введённый текст.
    if (!(value === null && error !== null)) {
      setDraft(value === null ? '' : String(value));
      setError(null);
    }
  }

  useReportInvalid(error !== null && !disabled);

  const commit = () => {
    const parsed = parseIntInput(draft, min);
    setError(parsed.error);
    if (parsed.error === null) setDraft(parsed.value === null ? '' : String(parsed.value));
    if (parsed.value !== value) onChange(parsed.value);
  };

  const classes = [navRow !== undefined ? 'nav-cell' : '', error ? 'input-invalid' : ''].filter(Boolean).join(' ');

  return (
    <Tooltip title={error ?? ''} open={error ? undefined : false} color="#cf1322">
      <Input
        ref={inputRef}
        value={draft}
        inputMode="numeric"
        autoFocus={autoFocus}
        disabled={disabled}
        status={error ? 'error' : undefined}
        aria-invalid={error ? true : undefined}
        className={classes || undefined}
        data-nav-row={navRow}
        data-nav-col={navCol}
        style={{ textAlign: 'right', width: '100%' }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          onKeyDown?.(e);
        }}
      />
    </Tooltip>
  );
}
